var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import mergeRefs from 'merge-refs';
import { forwardRef, useCallback, useMemo, useRef, useState, } from 'react';
import { InputPrivate, SIZE, useButtonNavigation, useClearButton } from '@snack-uikit/input-private';
import { extractSupportProps } from '@snack-uikit/utils';
import { VALIDATION_STATE } from '../../constants';
import { FieldContainerPrivate } from '../../helperComponents';
import { useCopyButton, usePostfix, usePostfixButton, usePrefix, usePrefixButton, useValueControl } from '../../hooks';
import { getValidationState } from '../../utils/getValidationState';
import { FieldDecorator } from '../FieldDecorator';
import { getContainerVariant } from './helpers';
export const FieldText = forwardRef((_a, ref) => {
    var { id, name, value: valueProp, placeholder, maxLength, disabled = false, readonly = false, showCopyButton: showCopyButtonProp = true, showClearButton: showClearButtonProp = true, allowMoreThanMaxLength = false, onChange: onChangeProp, onFocus, onBlur, onCopyButtonClick, className, label, labelTooltip, labelTooltipPlacement, required = false, caption, hint, showHintIcon, size = SIZE.S, validationState = VALIDATION_STATE.Default, error, autoComplete, prefixIcon, prefix, postfix, button: buttonProp, onPaste, onKeyDown, type = 'text' } = _a, rest = __rest(_a, ["id", "name", "value", "placeholder", "maxLength", "disabled", "readonly", "showCopyButton", "showClearButton", "allowMoreThanMaxLength", "onChange", "onFocus", "onBlur", "onCopyButtonClick", "className", "label", "labelTooltip", "labelTooltipPlacement", "required", "caption", "hint", "showHintIcon", "size", "validationState", "error", "autoComplete", "prefixIcon", "prefix", "postfix", "button", "onPaste", "onKeyDown", "type"]);
    const [value = '', onChange] = useValueControl({
        value: valueProp,
        defaultValue: '',
        onChange: onChangeProp,
    });
    const localRef = useRef(null);
    const clearButtonRef = useRef(null);
    const copyButtonRef = useRef(null);
    const showAdditionalButton = Boolean(value && !disabled);
    const showClearButton = showClearButtonProp && showAdditionalButton && !readonly;
    const showCopyButton = showCopyButtonProp && showAdditionalButton && readonly;
    const fieldValidationState = getValidationState({ validationState, error });
    const button = useMemo(() => buttonProp
        ? Object.assign(Object.assign({}, buttonProp), { selection: Object.assign(Object.assign({}, buttonProp.selection), { onChange: value => {
                    var _a, _b;
                    (_b = (_a = buttonProp.selection) === null || _a === void 0 ? void 0 : _a.onChange) === null || _b === void 0 ? void 0 : _b.call(_a, value);
                    setTimeout(() => { var _a; return (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
                } }) }) : undefined, [buttonProp]);
    const containerVariant = getContainerVariant({ button });
    const onClear = () => {
        var _a;
        onChange('');
        if (required) {
            (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    const clearButtonSettings = useClearButton({ clearButtonRef, showClearButton, size, onClear });
    const copyButtonSettings = useCopyButton({
        copyButtonRef,
        showCopyButton,
        size,
        valueToCopy: value,
        prefix: typeof prefix === 'string' ? prefix : undefined,
        postfix: typeof postfix === 'string' ? postfix : undefined,
        onCopyButtonClick,
    });
    const [isButtonFocused, setIsButtonFocused] = useState(false);
    const onButtonFocus = useCallback(() => setIsButtonFocused(true), []);
    const onButtonBlur = useCallback(() => setIsButtonFocused(false), []);
    const prefixSettings = usePrefix({ prefix, disabled });
    const prefixButtonSettings = usePrefixButton({
        button,
        prefixIcon,
        size,
        disabled,
        readonly,
        onFocus: onButtonFocus,
        onBlur: onButtonBlur,
    });
    const postfixSettings = usePostfix({ postfix, disabled });
    const postfixButtonSettings = usePostfixButton({
        button,
        size,
        disabled,
        readonly,
        onFocus: onButtonFocus,
        onBlur: onButtonBlur,
    });
    const { postfixButtons, prefixButtons, inputTabIndex, onInputKeyDown } = useButtonNavigation({
        inputRef: localRef,
        postfixButtons: useMemo(() => [clearButtonSettings, copyButtonSettings, postfixSettings, postfixButtonSettings], [clearButtonSettings, copyButtonSettings, postfixSettings, postfixButtonSettings]),
        prefixButtons: useMemo(() => [prefixButtonSettings, prefixSettings], [prefixButtonSettings, prefixSettings]),
        readonly,
        submitKeys: ['Enter', 'Space', 'Tab'],
    });
    const handleKeyDown = event => {
        onInputKeyDown(event);
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    };
    return (_jsx(FieldDecorator, Object.assign({ className: className, label: label, labelTooltip: labelTooltip, labelTooltipPlacement: labelTooltipPlacement, labelFor: id, required: required, caption: caption, length: maxLength ? { max: maxLength, current: value.length } : undefined, hint: hint, disabled: disabled, readonly: readonly, showHintIcon: showHintIcon, size: size, validationState: fieldValidationState, error: error }, extractSupportProps(rest), { children: _jsx(FieldContainerPrivate, { size: size, validationState: fieldValidationState, disabled: disabled, readonly: readonly, variant: containerVariant, inputRef: localRef, prefix: prefixButtons, postfix: postfixButtons, disableFocus: isButtonFocused, children: _jsx(InputPrivate, { ref: mergeRefs(ref, localRef), "data-size": size, value: value, onChange: onChange, onFocus: onFocus, onBlur: onBlur, tabIndex: inputTabIndex, onKeyDown: handleKeyDown, onPaste: onPaste, placeholder: placeholder, disabled: disabled, readonly: readonly, type: type, maxLength: allowMoreThanMaxLength ? undefined : maxLength || undefined, id: id, name: name, autoComplete: autoComplete, "data-test-id": 'field-text__input' }) }) })));
});
